import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Text } from '@perch-labs/shrike';
import { Container, Page } from 'styles';
import { BackLink } from 'components/back-link';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { DifferentUsernameAccordion } from 'pages/settings/components/different-username-accordion/different-username-accordion.component';
import { copyFor, routes } from 'config';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { RouterLocation } from 'types/shared';
import { SyncItem } from '../components/sync-item';
import { UtilityRow } from './utility-row';
import { PaginatedUtilityAccounts } from './paginated-utility-accounts';
import { GET_UTILITY_SYNC_CREDENTIAL_DATA } from './utility-sync-credential.api';

const getCopy = copyFor('settings.utilitySyncCredential');
const { utilitySync } = routes();

interface Props {
  location?: RouterLocation;
}

export const UtilitySyncCredential = ({ location }: Props) => {
  const { utilityCredentialId } = useParams<{ utilityCredentialId?: string }>();

  const { data, loading, error } = useQuery<
    GetUtilitySyncCredentialDataQuery,
    GetUtilitySyncCredentialDataQueryVariables
  >(GET_UTILITY_SYNC_CREDENTIAL_DATA, {
    skip: !utilityCredentialId,
    variables: { utilityCredentialId: Number(utilityCredentialId) || -1 },
  });

  const previousAppLocation = location?.state?.from;
  const backRoute = previousAppLocation || utilitySync;

  if (loading) return <LoadingPage />;
  if (error || !data) {
    return <ErrorPage title={getCopy('title')} to={backRoute} />;
  }

  const { utilityCredential } = data;
  const { utility } = utilityCredential;
  const utilityName = utility.displayName;

  return (
    <>
      <BackLink to={backRoute} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Container direction="column">
          <Text textStyle="heading800" tag="h1" margin={{ bottom: '48px' }}>
            {utilityName}
          </Text>
          <Text
            tag="h2"
            textStyle="eyebrow400"
            color="secondary"
            margin={{ bottom: '16px' }}
          >
            {getCopy('credentialDetails')}
          </Text>
          <SyncItem utilityCredential={utilityCredential} />
          <UtilityRow url={utility.url} utilityName={utilityName} />
          <Text
            tag="h2"
            textStyle="eyebrow400"
            color="secondary"
            margin={{ bottom: '24px', top: '80px' }}
          >
            {getCopy('utilityAccounts.title')}
          </Text>
          <PaginatedUtilityAccounts
            utilityCredentialId={utilityCredential.id}
          />
        </Container>
      </Page>
      {utility.overrideEmailIsUsername && <DifferentUsernameAccordion />}
    </>
  );
};
